import { FC } from "react";
import { color as themeColor } from "@styles";
import { HeadingSize, H2 } from "../../atoms/Heading";
import { P1 } from "../../atoms/Paragraph";
import styled from "styled-components";
import { RewardCardTabSwitcherDesktop } from "./RewardCardTabSwitcherDesktop";
import { RewardCardTabSwitcherMobile } from "./RewardCardTabSwitcherMobile";
import { Spacer } from "../../templates/Spacer";
import { StandardSection, StandardGap } from "../../templates/StandardSection";
import { tablet, mobile } from "../../templates/Breakpoints";
import { convertNewlinesToBreaks } from "utils/string";
import { RegionalReward } from "components/atoms/RewardCard";

export interface RewardTab {
  title?: string;
  rewards: RegionalReward[];
}

export const RewardCardTabSwitcher: FC<
  React.PropsWithChildren<{
    id?: string;
    title?: string | React.ReactNode;
    content: string | React.ReactNode;
    rewards: RegionalReward[];
    pt?: string;
    pb?: string;
    backgroundColor?: string;
    titleStyles?: { fontSize: string; align: "left" | "center" };
    contentStyles?: { fontSize: string; lineHeight: string; align: "left" | "center" };
  }>
> = ({ id, title, content, rewards, pt, pb, backgroundColor, titleStyles, contentStyles }) => {
  return (
    <Container
      backgroundColor={backgroundColor}
      id={id}
      aria-labelledby={`${id}-rewardcardtabswitcher-title`}
    >
      <StandardSection mobPt="4rem" pt={pt} pb="0" maxWidth="58rem">
        {title && (
          <H2
            id={`${id}-rewardcardtabswitcher-title`}
            size={HeadingSize.H2}
            fontSize={titleStyles?.fontSize}
            align={titleStyles?.align || "center"}
          >
            {convertNewlinesToBreaks(title)}
          </H2>
        )}
        <Padding>
          <P1
            fontSize={contentStyles?.fontSize}
            lineHeight={contentStyles?.lineHeight}
            align={contentStyles?.align}
          >
            {convertNewlinesToBreaks(content)}
          </P1>
        </Padding>
      </StandardSection>
      <Desktop>
        <RewardCardTabSwitcherDesktop backgroundColor={backgroundColor} rewards={rewards} pb={pb} />
      </Desktop>
      <Mobile>
        <RewardCardTabSwitcherMobile backgroundColor={backgroundColor} rewards={rewards} />
        {pb ? <Spacer height={pb} /> : <StandardGap />}
      </Mobile>
    </Container>
  );
};

const Padding = styled.section`
  padding: 0 6rem 1rem;

  ${tablet} {
    padding: 0 3rem;
  }

  ${mobile} {
    padding: 0 0.5rem;
  }
`;

const Container = styled.section<{ backgroundColor?: string }>`
  background: ${(p) => (p.backgroundColor ? p.backgroundColor : themeColor.S310)};
  text-align: center;
`;

const Desktop = styled.div`
  display: none;

  @media only screen and (min-width: 36rem) {
    display: block;
  }
`;

const Mobile = styled.div`
  display: none;

  @media only screen and (max-width: 36rem) {
    display: block;
  }
`;
